import { useState } from 'react';
import { isAxiosError } from 'axios';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useClearSiteCache, type CacheClearLayer } from '@/lib/adminApi';

/**
 * Operator cache-clear button (plan §3.3 / D13 — ADMIN/SUPPORT only, never
 * shown to clients). Idempotent, so there is no confirm dialog; it disables
 * while a flush is in flight. The success toast summarizes the per-layer
 * result and the last-cleared time is shown beneath the button.
 */
export interface ClearCacheButtonProps {
  websiteId: string;
  /** When set, the company's website list is refetched after a successful flush. */
  companyId?: string;
  /** Persisted last-cleared timestamp (ISO) from the website record, if known. */
  cacheLastClearedAt?: string | null;
  className?: string;
}

/** Build a compact human summary of the per-layer flush result. */
function summarize(layers: CacheClearLayer[]): string {
  const cleared = layers.filter((l) => l.status === 'cleared').map((l) => l.layer);
  const failed = layers.filter((l) => l.status === 'error').map((l) => l.layer);
  const parts: string[] = [];
  parts.push(cleared.length > 0 ? `cleared ${cleared.join(', ')}` : 'nothing to clear');
  if (failed.length > 0) parts.push(`failed: ${failed.join(', ')}`);
  return parts.join('; ');
}

/** Extract the server's clean error message, falling back to a generic one. */
function errorMessage(err: unknown): string {
  if (isAxiosError(err)) {
    const msg = (err.response?.data as { error?: { message?: string } } | undefined)?.error
      ?.message;
    if (msg) return msg;
  }
  return "Couldn't clear cache — try again";
}

export function ClearCacheButton({
  websiteId,
  companyId,
  cacheLastClearedAt = null,
  className,
}: ClearCacheButtonProps): JSX.Element {
  const clearMut = useClearSiteCache();
  const [lastCleared, setLastCleared] = useState<string | null>(cacheLastClearedAt);

  async function handleClick() {
    try {
      const res = await clearMut.mutateAsync({ websiteId, companyId });
      setLastCleared(res.cacheLastClearedAt);
      const summary = summarize(res.result.layers);
      const failed = res.result.layers.some((l) => l.status === 'error');
      if (failed) {
        toast.error(`Cache clear finished with errors — ${summary}`);
      } else {
        toast.success(`Cache cleared — ${summary}`);
      }
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  return (
    <div className={cn('flex flex-col items-start gap-1', className)}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleClick}
        disabled={clearMut.isPending}
      >
        <RefreshCw className={cn('mr-2 h-4 w-4', clearMut.isPending && 'animate-spin')} />
        {clearMut.isPending ? 'Clearing…' : 'Clear cache'}
      </Button>
      {lastCleared && (
        <span className="text-xs text-muted-foreground">
          Last cleared {new Date(lastCleared).toLocaleString()}
        </span>
      )}
    </div>
  );
}
